import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import apiService from '../services/apiService';
import { EmptyState } from '../components';
import { Calendar, Tag } from 'lucide-react';

const PLATFORM_FEE = 5;
const TAX_RATE = 0.05;
const DEMO_COUPONS = { SAVE5: 5, BIRYANI10: 10, FIRST15: 15 };

function formatDateISO(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function formatTimeDisplay(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${suffix}`;
}

function buildNextDays(count = 14) {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });
}

function shortDateLabel(d) {
  const today = new Date();
  today.setHours(0,0,0,0);
  const date = new Date(d);
  date.setHours(0,0,0,0);
  const diff = Math.round((date - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const CartPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    cartItems,
    cartRestaurant,
    removeFromCart,
    updateQuantity,
    updateCookingRequest,
    getCartTotal,
    clearCart,
  } = useCart();

  const [cookingInstructions, setCookingInstructions] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);

  // Pickup slots
  const [availability, setAvailability] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const kitchenId = cartItems[0]?.kitchenId || cartRestaurant?.id;
  const dates = buildNextDays(14);

  // Fetch availability
  useEffect(() => {
    if (!kitchenId) return;
    apiService.getKitchenAvailability(kitchenId).then(res => {
      if (res?.success && res.data) setAvailability(res.data);
    }).catch(() => {});
  }, [kitchenId]);

  // Fetch slots
  const fetchSlots = useCallback(async (kid, date) => {
    if (!kid) return;
    setSlotsLoading(true);
    setSelectedTime(null);
    const dateStr = formatDateISO(date);
    try {
      const res = await apiService.getKitchenPickupSlots(kid, dateStr);
      if (res?.success && res.data) {
        const now = new Date();
        const isToday = dateStr === formatDateISO(new Date());
        const available = (res.data.slots || []).filter(s => {
          if (!s.available) return false;
          if (isToday) {
            const [h, m] = (s.time || '').split(':').map(Number);
            if (isNaN(h)) return false;
            const st = new Date();
            st.setHours(h, m, 0, 0);
            return st > now;
          }
          return true;
        });
        setSlots(available);
        if (available.length > 0) setSelectedTime(available[0].time);
      } else {
        setSlots([]);
      }
    } catch (e) {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (kitchenId && selectedDate) fetchSlots(kitchenId, selectedDate);
  }, [kitchenId, selectedDate, fetchSlots]);

  const subtotal = getCartTotal();
  const tax = subtotal * TAX_RATE;
  const total = subtotal + PLATFORM_FEE + tax - discount;

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponError('Please enter a coupon code'); return; }
    setCouponLoading(true);
    setCouponError('');
    try {
      if (DEMO_COUPONS[code]) {
        const amount = DEMO_COUPONS[code];
        setDiscount(amount);
        setAppliedCoupon({ code, amount });
        setCouponInput('');
        setShowCouponInput(false);
      } else {
        setCouponError('Invalid coupon code. Try SAVE5, BIRYANI10, or FIRST15');
      }
    } finally {
      setCouponLoading(false);
    }
  };

  const handleCheckout = () => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    if (cartItems.length === 0) { alert('Your cart is empty'); return; }
    if (!selectedDate || !selectedTime) { alert('Please select a pickup date and time'); return; }

    navigate('/customer/checkout', {
      state: {
        cartItems,
        cartRestaurant,
        subtotal,
        discount,
        appliedCoupon,
        selectedDate: selectedDate.toISOString(),
        selectedTime,
        selectedTimeDisplay: formatTimeDisplay(selectedTime),
        cookingInstructions,
        kitchenId,
      },
    });
  };

  if (cartItems.length === 0) {
    return (
      <div className="page-container">
        <EmptyState
          icon="🛒"
          title="Your cart is empty"
          subtitle="Add items from a kitchen to get started"
          action={{ label: 'Browse Kitchens', onClick: () => navigate('/customer') }}
        />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h2 className="cart-title">{cartRestaurant?.name || 'Your Cart'}</h2>
          <p className="cart-subtitle">{cartItems.reduce((s,i)=>s+i.quantity,0)} items · Pickup order</p>
        </div>
      </div>

      <div className="cart-content">
        {/* Cart Items */}
        <div className="cart-section">
          <h3 className="cart-section-title">Order Items</h3>
          {cartItems.map(item => (
            <div key={item.id} className="cart-item-row">
              <div className="cart-item-info">
                <h4 className="cart-item-name">{item.name}</h4>
                <p className="cart-item-price">${(item.price * item.quantity).toFixed(2)}</p>
                {/* Cooking request */}
                <input
                  type="text"
                  className="cooking-request-input"
                  placeholder="Add cooking request (e.g. extra spicy)..."
                  value={item.cookingRequest || ''}
                  onChange={(e) => updateCookingRequest(item.id, e.target.value)}
                />
              </div>
              <div className="cart-item-controls">
                <div className="qty-controls">
                  <button className="qty-btn" onClick={() => {
                    if (item.quantity === 1) removeFromCart(item.id);
                    else updateQuantity(item.id, item.quantity - 1);
                  }}>−</button>
                  <span className="qty-value">{item.quantity}</span>
                  <button className="qty-btn" onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cooking Instructions */}
        <div className="cart-section">
          <h3 className="cart-section-title">Cooking Instructions</h3>
          <textarea
            className="cooking-instructions"
            placeholder="Add any special instructions for your entire order..."
            value={cookingInstructions}
            onChange={(e) => setCookingInstructions(e.target.value)}
            rows={3}
          />
        </div>

        {/* Pickup Time */}
        <div className="cart-section">
          <h3 className="cart-section-title"><Calendar size={16} /> Select Pickup Time</h3>
          <div className="date-scroll">
            {dates.map((date, idx) => {
              const isSelected = formatDateISO(date) === formatDateISO(selectedDate);
              return (
                <button
                  key={idx}
                  className={`date-chip ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedDate(date)}
                >
                  {shortDateLabel(date)}
                </button>
              );
            })}
          </div>

          {slotsLoading ? (
            <p className="slots-loading">Loading available slots...</p>
          ) : slots.length === 0 ? (
            <p className="no-slots">No pickup slots available for {shortDateLabel(selectedDate)}</p>
          ) : (
            <div className="slots-grid">
              {slots.map((slot, idx) => (
                <button
                  key={idx}
                  className={`slot-chip ${selectedTime === slot.time ? 'selected' : ''}`}
                  onClick={() => setSelectedTime(slot.time)}
                >
                  {formatTimeDisplay(slot.time)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Coupon */}
        <div className="cart-section">
          <div className="coupon-header">
            <h3 className="cart-section-title"><Tag size={16} /> Coupon</h3>
            {!appliedCoupon && (
              <button className="apply-coupon-toggle" onClick={() => setShowCouponInput(!showCouponInput)}>
                {showCouponInput ? 'Cancel' : 'Apply Coupon'}
              </button>
            )}
          </div>
          {appliedCoupon ? (
            <div className="coupon-applied">
              <span>✅ {appliedCoupon.code} — Saved ${appliedCoupon.amount}</span>
              <button className="remove-coupon" onClick={() => { setDiscount(0); setAppliedCoupon(null); }}>✕</button>
            </div>
          ) : showCouponInput ? (
            <div className="coupon-input-row">
              <input
                type="text"
                className="form-input"
                placeholder="Enter coupon code"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
              />
              <button className="btn-primary" onClick={handleApplyCoupon} disabled={couponLoading}>
                {couponLoading ? '...' : 'Apply'}
              </button>
            </div>
          ) : null}
          {couponError && <p className="coupon-error">{couponError}</p>}
        </div>

        {/* Price Summary */}
        <div className="cart-section price-summary">
          <h3 className="cart-section-title">Bill Details</h3>
          <div className="price-row">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="price-row">
            <span>Platform Fee</span>
            <span>${PLATFORM_FEE.toFixed(2)}</span>
          </div>
          <div className="price-row">
            <span>Tax (5%)</span>
            <span>${tax.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="price-row discount">
              <span>Discount</span>
              <span>-${discount.toFixed(2)}</span>
            </div>
          )}
          <hr className="price-divider" />
          <div className="price-row total">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Checkout Button */}
      <div className="cart-checkout-bar">
        {selectedTime && (
          <p className="selected-time-label">
            Pickup: {shortDateLabel(selectedDate)} at {formatTimeDisplay(selectedTime)}
          </p>
        )}
        <button
          className="btn-primary btn-full checkout-btn"
          onClick={handleCheckout}
          disabled={!selectedTime}
        >
          Proceed to Checkout · ${total.toFixed(2)}
        </button>
      </div>
    </div>
  );
};

export default CartPage;
